import React, {useCallback, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import ReactSelect, {components, OptionProps} from 'react-select';
import {DateTime} from 'luxon';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import TableMetronic from '../../../../app/common/components/table/TableMetronic';
import { useGetTelephonyDetails } from '../../Hooks/TelephonieManager';
import {useGetServiceInfo} from '../../Hooks/ServiceManager';
import {PageTitle} from '../../../../_metronic/layout/core';
import TelephonyServiceHeader from './TelephonyServiceHeader';
import {getTelephonyDetailsPathname} from '../../Helpers/ServiceHelper';
import {TelephonyCalls} from '../../Types/type';
import { readablePriceHelper } from '../../Helpers/readablePrice';
import { readableTelephoneHelper } from '../../Helpers/readableTelephone';
import { seconds2durationHelper } from '../../Helpers/seconds2duration';
import styles from '../Services.module.scss';

type CallFilterField = 'appelant' | 'appele' | 'nom' | 'jour';

type CallFilterKeys = Record<CallFilterField, string>;

interface CallFilterOption {
    value: string;
    label: string;
    count: number;
}

type CallFilters = Record<CallFilterField, CallFilterOption[]>;

const EMPTY_FILTERS: CallFilters = {appelant: [], appele: [], nom: [], jour: []};

const FILTER_PLACEHOLDERS: Record<CallFilterField, string> = {
    appelant: 'TELEPHONIE.APPELS.FILTER.ALL_APPELANTS',
    appele: 'TELEPHONIE.APPELS.FILTER.ALL_APPELES',
    nom: 'TELEPHONIE.APPELS.FILTER.ALL_NOMS',
    jour: 'TELEPHONIE.APPELS.FILTER.ALL_DATES',
};

const filterSelectStyles = {
    menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

// Date d'appel, qu'elle soit ISO, SQL ou un objet {date: …}
const parseCallDate = (value: unknown): DateTime | null => {
    const raw = typeof value === 'object' && value !== null ? (value as {date?: string}).date : value;
    if (typeof raw !== 'string' || raw === '') return null;
    const parsed = DateTime.fromISO(raw).isValid ? DateTime.fromISO(raw) : DateTime.fromSQL(raw);
    return parsed.isValid ? parsed : null;
};

// Jour (yyyy-MM-dd) d'une date d'appel
const toIsoDay = (value: unknown): string => parseCallDate(value)?.toISODate() ?? '';

// Même rendu que la date des appels sortants (chaîne SQL renvoyée par CIS)
const toCallDateTime = (value: unknown): string => parseCallDate(value)?.toFormat('yyyy-MM-dd HH:mm:ss') ?? '';

const matchesFilters = (filters: CallFilters, keys: CallFilterKeys): boolean =>
    (Object.keys(filters) as CallFilterField[]).every(
        (field) => filters[field].length === 0 || filters[field].some((o) => o.value === keys[field])
    );

const CheckboxOption = (props: OptionProps<CallFilterOption, true>) => (
    <components.Option {...props}>
        <div className="d-flex align-items-start">
            <input
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
                className="me-2 mt-1 flex-shrink-0"
            />
            <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
                {props.label}
            </label>
            <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
        </div>
    </components.Option>
);

interface CallFiltersToolbarProps {
    rows: CallFilterKeys[];
    filters: CallFilters;
    onChange: (filters: CallFilters) => void;
    formatPhone: (value: string) => string;
    formatName: (value: string) => string;
}

const CallFiltersToolbar = ({
    rows,
    filters,
    onChange,
    formatPhone,
    formatName,
}: CallFiltersToolbarProps): JSX.Element => {
    const intl = useIntl();

    const options = useMemo<Record<CallFilterField, CallFilterOption[]>>(() => {
        const formatDay = (day: string) =>
            DateTime.fromISO(day).setLocale(intl.locale).toLocaleString(DateTime.DATE_SHORT);
        const labelFormatters: Record<CallFilterField, (value: string) => string> = {
            appelant: formatPhone,
            appele: formatPhone,
            nom: formatName,
            jour: formatDay,
        };

        const build = (field: CallFilterField): CallFilterOption[] => {
            const map = new Map<string, CallFilterOption>();
            rows.forEach((row) => {
                const value = row[field];
                if (!value) return;
                const existing = map.get(value);
                if (existing) {
                    existing.count += 1;
                    return;
                }
                map.set(value, {value, label: labelFormatters[field](value), count: 1});
            });
            const list = Array.from(map.values());
            return field === 'jour'
                ? list.sort((a, b) => b.value.localeCompare(a.value))
                : list.sort((a, b) => a.label.localeCompare(b.label));
        };

        return {appelant: build('appelant'), appele: build('appele'), nom: build('nom'), jour: build('jour')};
    }, [rows, intl.locale, formatPhone, formatName]);

    return (
        <>
            {(Object.keys(FILTER_PLACEHOLDERS) as CallFilterField[]).map((field) => (
                <div key={field} className={styles.filterSelectWrapper}>
                    <ReactSelect<CallFilterOption, true>
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{Option: CheckboxOption}}
                        options={options[field]}
                        value={filters[field]}
                        onChange={(vals) => onChange({...filters, [field]: vals ? [...vals] : []})}
                        placeholder={intl.formatMessage({id: FILTER_PLACEHOLDERS[field]})}
                        noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
                        classNamePrefix="filter-select"
                        menuPortalTarget={document.body}
                        styles={filterSelectStyles}
                    />
                </div>
            ))}
        </>
    );
};

const DetailsAppels = (): JSX.Element => {
    const intl = useIntl();
    const readablePriceHelperInstance = useMemo(() => new readablePriceHelper(), []);
    const readableTelephoneHelperInstance = useMemo(() => new readableTelephoneHelper(), []);
    const seconds2durationHelperInstance = useMemo(() => new seconds2durationHelper(), []);
    const readableUserName = useCallback(
      (name: string) => {
        // Le nom d'utilisateur est parfois un numéro (+33…) : on le formate comme un N° appelé
        const value = String(name ?? '');
        return /^\+?33\d{9}$/.test(value)
          ? readableTelephoneHelperInstance.readableTelephone(value.replace(/^\+/, ''))
          : value;
      },
      [readableTelephoneHelperInstance]
    );
    const [searchParams] = useSearchParams();

    const type = searchParams.get('type') || "";
    const caller = searchParams.get('caller') || '';
    const destination = searchParams.get('destination') || '';
    const month = searchParams.get('month') || '';
    const {serviceId} = useParams<{serviceId: string}>();
    const label = searchParams.get('label') || '';
    const navigate = useNavigate();

    const {data: dataService} = useGetServiceInfo(serviceId ?? '');

    const selectionDetails = useMemo(() => {
      const monthDate = DateTime.fromFormat(month, 'yyyy-MM', {locale: intl.locale});
      const monthLabel = monthDate.isValid ? monthDate.toFormat('LLLL yyyy') : month;
      const values = {
        month: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
        account: readableTelephoneHelperInstance.readableTelephone(label),
        number: readableTelephoneHelperInstance.readableTelephone(caller),
        destination: label,
      };
      return caller !== ''
        ? intl.formatMessage({id: 'TELEPHONIE.APPELS.SELECTION.COMPTE'}, values)
        : intl.formatMessage({id: 'TELEPHONIE.APPELS.SELECTION.DESTINATION'}, values);
    }, [intl, month, label, caller, readableTelephoneHelperInstance]);

    const goBackToConsommations = () => {
      const params = new URLSearchParams(searchParams);
      ['type', 'destination', 'caller', 'label'].forEach((key) => params.delete(key));
      params.set('tab', 'consodetaillees');
      navigate({pathname: getTelephonyDetailsPathname(serviceId ?? ''), search: params.toString()});
    };

    const {data, isLoading} = useGetTelephonyDetails({category:type, caller:caller , destination:destination , month: month, serviceId });

    const [filtersSortant, setFiltersSortant] = useState<CallFilters>(EMPTY_FILTERS);
    const [filtersEntrant, setFiltersEntrant] = useState<CallFilters>(EMPTY_FILTERS);

    const formatPhone = useCallback(
      (value: string) => readableTelephoneHelperInstance.readableTelephone(value),
      [readableTelephoneHelperInstance]
    );

    const outgoingKeys = useMemo(
      () =>
        (data?.outgoings || []).map((row) => ({
          row,
          keys: {
            appelant: String(row.sipAccount ?? ''),
            appele: String(row.called ?? ''),
            nom: String(row.clid ?? ''),
            jour: toIsoDay(row.callDate),
          },
        })),
      [data]
    );

    const incomingKeys = useMemo(
      () =>
        (data?.incomings || []).map((row: any) => ({
          row,
          keys: {
            appelant: String(row.sipAccount || row.src || ''),
            appele: String(row.dst ?? ''),
            nom: String(row.callername ?? ''),
            jour: toIsoDay(row.calldate ?? row.date),
          },
        })),
      [data]
    );

    const outgoingFilterRows = useMemo(() => outgoingKeys.map(({keys}) => keys), [outgoingKeys]);
    const incomingFilterRows = useMemo(() => incomingKeys.map(({keys}) => keys), [incomingKeys]);

    const outgoings = useMemo(
      () => outgoingKeys.filter(({keys}) => matchesFilters(filtersSortant, keys)).map(({row}) => row),
      [outgoingKeys, filtersSortant]
    );

    const incomings = useMemo(
      () => incomingKeys.filter(({keys}) => matchesFilters(filtersEntrant, keys)).map(({row}) => row),
      [incomingKeys, filtersEntrant]
    );


    const columnsSortant = useMemo<ColumnDef<TelephonyCalls>[]>(() => {
          return [
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.DATE_HEURE'}),
              accessorKey: 'callDate',
            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.APPELANT'}),
              accessorKey: 'sipAccount',
              cell: (info) => {
                  return readableTelephoneHelperInstance.readableTelephone(info.row.original.sipAccount);
              },
            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.NOM_UTILISATEUR'}),
              accessorKey: 'clid',
              cell: (info) => readableUserName(info.row.original.clid),
            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.NUMERO_APPELE'}),
              accessorKey: 'called',
              cell: (info) => {
                  return readableTelephoneHelperInstance.readableTelephone(info.row.original.called);
              },
            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.CONSO.COL.DUREE_FACTUREE'}),
              accessorKey: 'time',
              cell: (info) => {
                  return seconds2durationHelperInstance.seconds2duration(info.row.original.time ?? 0, true);
              },
            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.MONTANT_HT'}),
              accessorKey: 'price',
              cell: (info) => {
                  return readablePriceHelperInstance.readablePrice(info.row.original.price ?? 0) + " €";
              }
            },
          ];
        }, [intl, readableTelephoneHelperInstance, readableUserName, seconds2durationHelperInstance, readablePriceHelperInstance]
      )
    ;


    const columnsEntrant = useMemo<ColumnDef<any>[]>(() => {
          return [
            {
              id: 'calldate',
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.DATE_HEURE'}),
              accessorFn: (row) => toCallDateTime(row.calldate),
            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.APPELANT'}),
              accessorKey: 'sipAccount',
              cell: (info) => {
                if (info.row.original.sipAccount) return readableTelephoneHelperInstance.readableTelephone(info.row.original.sipAccount);
                else return readableTelephoneHelperInstance.readableTelephone(info.row.original.src);
              }
              
            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.NOM_UTILISATEUR'}),
              accessorKey: 'callername',
              cell: (info) => readableUserName(info.row.original.callername),

            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.NUMERO_APPELE'}),
              accessorKey: 'dst',
              cell: (info) => {
                  return readableTelephoneHelperInstance.readableTelephone(info.row.original.dst);
              },
            },
            {
              header: intl.formatMessage({id: 'TELEPHONIE.APPELS.COL.DUREE'}),
              accessorKey: 'billsec',
              cell: (info) => {
                  return seconds2durationHelperInstance.seconds2duration(info.row.original.billsec ?? 0, true);
              }
            }
          ];
        }, [intl, readableTelephoneHelperInstance, readableUserName, seconds2durationHelperInstance]
      )
    ;

   
    return (
      <>
        <PageTitle breadcrumbs={[]}>{dataService?.link_name}</PageTitle>

        {dataService != null && (
          <TelephonyServiceHeader
            service={dataService}
            onBack={goBackToConsommations}
            details={selectionDetails}
          />
        )}

        <TableMetronic<TelephonyCalls>
          title={intl.formatMessage({id: 'TELEPHONIE.APPELS.SORTANTS.TITLE'})}
          data={outgoings}
          columns={columnsSortant}
          toolbar={
            <CallFiltersToolbar
              rows={outgoingFilterRows}
              filters={filtersSortant}
              onChange={setFiltersSortant}
              formatPhone={formatPhone}
              formatName={readableUserName}
            />
          }
          stackedHeader
          isLoading={isLoading}
          searchBar
          pagination
        />

        <div className="mt-8">
          <TableMetronic<any>
            title={intl.formatMessage({id: 'TELEPHONIE.APPELS.ENTRANTS.TITLE'})}
            data={incomings}
            columns={columnsEntrant}
            toolbar={
              <CallFiltersToolbar
                rows={incomingFilterRows}
                filters={filtersEntrant}
                onChange={setFiltersEntrant}
                formatPhone={formatPhone}
                formatName={readableUserName}
              />
            }
            stackedHeader
            isLoading={isLoading}
            searchBar
            pagination
          />
        </div>
      </>
    );
  }
;

export default DetailsAppels;
